import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import addGreyIcon from "../../assets/add_grey.svg";
import { ArrowIcon, BackArrowIcon, CloseIcon } from "../../assets/assets";
import { isValidArray, isValidString } from "../../services/validators";
import HandleBackClick from "../HandleBackClick/HandleBackClick";
import InputBox from "../InputBox/InputBox";
import "./Select.css";

function Select(props) {
  const [chips, setChips] = useState([]);
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);
  const selectedChipIndexRef = useRef(null);
  const selectCompRef = useRef(null);
  const label = useRef(null);
  const [showBottomModal, setShowBottomModal] = useState(false);
  // set chips
  useEffect(() => {
    if (props.chips) {
      setChips([...props.chips]);
    }
  }, [props.chips]);

  const onChipClick = (event, index) => {
    event.stopPropagation();
    if (selectedChipIndex === index) {
      const newArray = [...chips];
      newArray.splice(index, 1);
      typeof props.onChipAdded === "function" &&
        props.onChipAdded([...newArray]);
      setSelectedChipIndex(null);
      selectedChipIndexRef.current = null;
      return;
    }
    selectedChipIndexRef.current = index;
    setSelectedChipIndex(index);
  };

  return (
    <>
      <div
        style={{
          height: "78px",
          marginColor: "white"
        }}
        className={`position-relative inherit-parent-width select-border-bottom-default
        ${props.className || ""}
        ${props.disabled === true ? " background-color-grey " : ""}`}
        data-cy={
          props["data-cy"] ? props["data-cy"] : `${props.name}-select-box-comp`
        }
        ref={selectCompRef}
        onClick={() => {
          if (props.disabled) {
            return;
          }
          label.current?.classList.add("active");
          setShowBottomModal(true);
        }}
      >
        <label
          data-cy={`label-${props.name}`}
          ref={label}
          className={`background-white font-color-secondary select-label position-absolute font-family-gilroy-medium font-size-medium inherit-parent-width padding-top-small ${
            props.labelClassName || ""
          } 
          ${props.disabled === true ? " background-color-grey " : ""}
          ${
            isValidArray(chips) || props.alwaysActive === true ? "active" : ""
          } ${props.disabled ? " disabled " : ""}
          `}
        >
          {props.label}
        </label>
        <div className="overflow-scroll inherit-parent-height">
          {isValidArray(chips) && (
            <section
              style={{ rowGap: "4px", columnGap: "10px" }}
              className="flex-direction-row flex-wrap hide-scroll-bar padding-top-larger padding-bottom-default padding-right-larger padding-left-small"
              data-cy="chips-container"
            >
              {isValidArray(chips) &&
                chips.map((chipData, index) => (
                  <div
                    style={{
                      marginTop: "6px"
                    }}
                    data-cy={`${chipData}-chip${
                      selectedChipIndex === index ? "-selected" : ""
                    }`}
                    className={`text-transform-capitalize text-overflow-ellipsis padding-top-smaller padding-bottom-smaller padding-left-default padding-right-default border-2px-lite-grey text-transform-uppercase border-radius-default font-size-smaller font-family-gilroy-regular font-color-secondary ${
                      selectedChipIndex === index
                        ? "background-color-lite-grey"
                        : ""
                    } flex-center-children-vertically `}
                    key={`chip-data-${index}`}
                    onClick={(event) => onChipClick(event, index)}
                  >
                    <span>{chipData}</span>
                    {selectedChipIndex === index && (
                      <div className="margin-left-default height-width-0-point-5em">
                        <CloseIcon color="black" height="10" width="10" />
                      </div>
                    )}
                  </div>
                ))}
            </section>
          )}
        </div>

        <div
          className="select-suffix-icon"
          data-cy={`${props?.name}-select-toggle-icon`}
        >
          <ArrowIcon />
        </div>
      </div>

      <AnimatedBottomModal
        showAddOption={props.showAddOption}
        show={showBottomModal}
        onClickClose={() => {
          if (props.disabled) {
            return;
          }
          setShowBottomModal(false);
        }}
        selectCompRef={selectCompRef}
        chips={chips}
        modalTitle={props.modalTitle}
        selectedChipIndex={selectedChipIndex}
        setSelectedChipIndex={setSelectedChipIndex}
        selectedChipIndexRef={selectedChipIndexRef}
        label={label}
        onChipAdded={props.onChipAdded}
        name={props.name}
        options={props.options}
        multiSelect={props.multiSelect}
        showSelectAll={props.showSelectAll}
        onChipClick={onChipClick}
        loading={props.loading}
        maxLength={props.maxLength}
      />
    </>
  );
}

Select.propTypes = {
  /**
   * array of inputs
   */
  chips: PropTypes.arrayOf(PropTypes.string),
  /**
   * callback function which returns the updated chip input array
   */
  onChipAdded: PropTypes.func,
  /**
   * disabled state of the select component
   */
  disabled: PropTypes.bool,
  /**
   * selector attribute for cypress testing , default value is input name with `-select-box-comp`
   */
  "data-cy": PropTypes.string,
  /**
   * name attribute of the select input element
   */
  name: PropTypes.string,
  /**
   * class name for the element
   */
  className: PropTypes.string,
  /**
   * class name for the label element
   */
  labelClassName: PropTypes.string,
  /**
   * sets label to active state
   */
  alwaysActive: PropTypes.bool,
  /**
   * content for the label element
   */
  label: PropTypes.string,
  /**
   * AnimatedBottomModal title
   */
  modalTitle: PropTypes.string,
  /**
   * pre defined options for select option
   */
  options: PropTypes.arrayOf(PropTypes.string),
  /**
   * shows loading suspense for the component
   */
  loading: PropTypes.bool,
  /**
   * which enables the multi select options
   */
  multiSelect: PropTypes.bool,
  /**
   * Hide/show add option in select dropdown
   */
  showAddOption: PropTypes.bool,
  /**
   * shows a "Select all" option at the top of the list (multiSelect only)
   */
  showSelectAll: PropTypes.bool,
  maxLength: PropTypes.number
};

Select.defaultProps = {
  onChipAdded: undefined,
  showAddOption: true,
  chips: [],
  disabled: false,
  "data-cy": undefined,
  name: "default",
  labelClassName: "",
  alwaysActive: false,
  label: "",
  modalTitle: "",
  options: [],
  loading: false,
  multiSelect: false,
  showSelectAll: false,
  maxLength: 25
};
export default Select;

function AnimatedBottomModal(props) {
  const [entranceAnimation, setEntranceAnimation] = useState(false);
  const childElementRef = useRef(null);
  // const bottomCardRef = useRef(null);
  const [value, setValue] = useState("");
  const inputRef = useRef(null);
  const [searchedOptions, setSearchedOptions] = useState([]);
  const [complete, setComplete] = useState(false);
  const previousLoadingState = useRef(false);

  const closeModal = () => {
    setEntranceAnimation(false);
    setTimeout(() => {
      props.onClickClose();
    }, 80);
  };
  // set searched options
  useEffect(() => {
    setSearchedOptions([...props.options]);
    // eslint-disable-next-line
  }, []);

  // set complete status
  useEffect(() => {
    if (previousLoadingState.current === true && props.loading === false) {
      setComplete(true);
    } else if (complete) {
      setComplete(false);
    }
    previousLoadingState.current = props.loading;
    // eslint-disable-next-line
  }, [props.loading]);

  //set animation
  useEffect(() => {
    if (props.show === true) {
      setEntranceAnimation(true);
      inputRef.current?.focus();
    }
    if (props.closeModal) {
      props.closeModal(closeModal);
    }
    if (!props.show && !isValidArray(props.chips)) {
      props.label.current?.classList.remove("active");
    }
    // eslint-disable-next-line
  }, [props.show]);

  // handle click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (!(
        childElementRef.current?.children[0].contains(event.target) ||
        props.selectCompRef.current?.contains(event.target)
      )) {
        closeModal();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line
  }, [childElementRef]);

  const onItemSelect = (item) => {
    let newList = [];
    if (props.multiSelect === true) {
      inputRef.current?.focus();
      const prevState = [...props.chips];
      newList = prevState.includes(item)
        ? prevState.filter((e) => e !== item)
        : [item, ...prevState];
      if (newList.length > 0) {
        props.label.current.classList.add("active");
      } else {
        props.label.current.classList.remove("active");
      }
    } else {
      const prevState = [...props.chips];
      newList = prevState.includes(item) ? [] : [item];
    }
    props.onChipAdded && props.onChipAdded([...newList]);
  };

  const allOptionsSelected =
    isValidArray(props.options) &&
    props.options.every((option) => props.chips.includes(option));

  // toggles every predefined option, keeping any custom chips
  const onSelectAll = () => {
    const customChips = props.chips.filter(
      (chip) => !props.options.includes(chip)
    );
    const newList = allOptionsSelected
      ? customChips
      : [...props.options, ...customChips];
    if (newList.length > 0) {
      props.label.current?.classList.add("active");
    } else {
      props.label.current?.classList.remove("active");
    }
    props.onChipAdded && props.onChipAdded([...newList]);
  };

  // true for show
  // False for not to show
  const shouldShowAddOptionButton = () => {
    let result = true;
    if (!isValidArray(searchedOptions)) {
      if (isValidArray(props.chips)) {
        props.chips.some((chip) => {
          const _returnResult = chip.toLowerCase() === value.toLowerCase();
          if (_returnResult) {
            result = false;
          }
          return _returnResult;
        });
      } else {
        result = true;
      }
    } else if (isValidString(value) && isValidArray(searchedOptions)) {
      if (isValidArray(props.chips)) {
        props.chips.some((chip) => {
          const _returnResult = chip.toLowerCase() === value.toLowerCase();
          if (_returnResult) {
            result = false;
          }
          return _returnResult;
        });
      } else {
        searchedOptions.some((option) => {
          const _returnResult = option.toLowerCase() === value.toLowerCase();
          if (_returnResult) {
            result = false;
          }
          return _returnResult;
        });
      }
      // return result;
    } else {
      result = false;
    }

    return result;
  };

  const onSearch = (text) => {
    if (text.length === 0) {
      setSearchedOptions([...props.options]);
      return;
    }
    text = text.toLowerCase().split(" ");
    const searchedList = props.options.filter((item) => {
      return text.every((el) => {
        return item?.toLowerCase()?.includes(el);
      });
    });
    setSearchedOptions([...searchedList]);
  };

  return (
    <HandleBackClick
      onClose={() => {
        props.onClickClose();
      }}
      isBackEnabled={props.show}
    >
      {props.show && (
        <div
          className="background-color-black-with-opacity-light inherit-parent-height inherit-parent-width position-fixed z-index-1 top-0 left-0 "
          data-cy="animated-bottom-modal"
        >
          <div
            className={` inherit-parent-height inherit-parent-width display-flex justify-content-flex-end align-items-flex-end  animated-bottom-modal-anime ${
              entranceAnimation ? "animated-bottom-modal-anime-entrance" : ""
            }`}
            ref={childElementRef}
          >
            <div className="padding-top-default padding-left-large padding-right-large inherit-parent-width flex-center-children-vertically flex-direction-column inherit-parent-height background-white">
              <div className="display-flex padding-right-large flex-justify-content-space-between flex-align-items-center inherit-parent-width">
                <div>
                  <div
                    className="inherit-parent-width padding-top-default padding-bottom-default background-color-white flex-direction-row justify-content-flex-start flex-center-children-vertically cursor-pointer"
                    onClick={(event) => {
                      event.preventDefault();
                      closeModal();
                    }}
                    data-cy="close-modal-btn"
                  >
                    <BackArrowIcon />
                  </div>
                </div>
                <h5
                  className="text-transform-uppercase font-weight-normal font-size-medium font-family-gilroy-regular font-color-secondary margin-top-default margin-bottom-default"
                  data-cy="modal-title"
                >
                  {props.modalTitle}
                </h5>
                <div className="padding-default"> </div>
              </div>
              {isValidArray(props.chips) && (
                <div className="height-20-percentage overflow-scroll">
                  <section
                    className="inherit-parent-width flex-direction-row gap-0-point-5em flex-wrap overflow-auto padding-top-default padding-bottom-default"
                    data-cy="modal-chips-container"
                  >
                    {props.chips.map((chipData, index) => (
                      <div
                        data-cy={`modal-${chipData}-chip${
                          props.selectedChipIndex === index ? "-selected" : ""
                        }`}
                        className={`text-transform-capitalize text-overflow-ellipsis padding-small border-2px-lite-grey text-transform-uppercase border-radius-default font-size-small font-family-gilroy-regular font-color-secondary ${
                          props.selectedChipIndex === index
                            ? "background-color-lite-grey"
                            : ""
                        } flex-center-children-vertically `}
                        key={`chip-data-${index}`}
                        onClick={(event) => props.onChipClick(event, index)}
                      >
                        <span>{chipData}</span>
                        {props.selectedChipIndex === index && (
                          <div className="margin-left-default height-width-0-point-5em">
                            <CloseIcon color="black" height="10" width="10" />
                          </div>
                        )}
                      </div>
                    ))}
                  </section>
                </div>
              )}
              <section className="inherit-parent-width position-relative">
                <InputBox
                  className={`font-size-medium padding-default inherit-parent-width`}
                  type="text"
                  name="search"
                  label="Search"
                  value={value}
                  onChange={(event) => {
                    event.preventDefault();
                    setValue(event.target.value);
                    onSearch(event.target.value);
                  }}
                  removeResponsive={true}
                  maxLength={props.maxLength ? props.maxLength : 25}
                  // ref={inputRef}
                  autoComplete="off"
                  loading={props.loading}
                />
              </section>

              <div
                data-cy={`${props.name}-options-section`}
                className={`inherit-parent-width margin-bottom-large background-color-white hide-scroll-bar overflow-auto ${
                  isValidArray(props.chips)
                    ? "flex-basis-70-percentage "
                    : "flex-basis-80-percentage "
                } `}
              >
                <ul className="list-style-type-none ">
                  <li className="hide-scroll-bar">
                    {props.multiSelect === true &&
                      props.showSelectAll === true &&
                      !isValidString(value) &&
                      isValidArray(props.options) && (
                        <ListItem
                          data="Select all"
                          onItemSelect={onSelectAll}
                          selected={allOptionsSelected}
                        />
                      )}
                    {searchedOptions.map((data, index) => {
                      return (
                        <ListItem
                          data={data}
                          key={index}
                          onItemSelect={onItemSelect}
                          selected={props.chips.includes(data)}
                        />
                      );
                    })}
                  </li>
                  <li>
                    {props.showAddOption !== false &&
                      shouldShowAddOptionButton() === true && (
                        <button
                          className="inherit-parent-width padding-top-default padding-bottom-default background-color-white flex-direction-row flex-justify-content-space-between"
                          onClick={() => {
                            const newArray = [value, ...props.chips];
                            props.onChipAdded?.([...newArray]);
                            setValue("");
                            setSearchedOptions([...props.options]);
                            inputRef.current?.focus();
                          }}
                          data-cy="add-custom-value-button"
                        >
                          <span className="font-family-gilroy-regular font-size-medium font-color-secondary ">
                            {`Add  "${value}"`}
                          </span>
                          <img src={addGreyIcon} alt="add" />
                        </button>
                      )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </HandleBackClick>
  );
}

function ListItem(props) {
  const [selected, setSelected] = useState(false);
  // set selected data
  useEffect(() => {
    setSelected(props.selected);
  }, [props.selected]);

  return (
    <div
      className="padding-small"
      onClick={() => {
        setSelected((prevState) => !prevState);
        props.onItemSelect(props.data);
      }}
      data-cy={`${props.data}-select-option-item`}
    >
      <div className="flex-center-children-vertically flex-justify-content-space-between">
        <span className="font-family-gilroy-regular font-size-medium font-color-secondary ">
          {props.data}
        </span>
        <CheckIcon data={props.data} selected={selected} />
      </div>
    </div>
  );
}

const CheckIcon = (props) => {
  return props.selected ? (
    <svg
      data-cy={`${props.data}-selected`}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="#00a000"
      className="bi bi-check2"
      viewBox="0 0 16 16"
    >
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ) : null;
};
